import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { createProductCategory, updateProductCategory } from "../services/catalogService";
import Toggle from "./Toggle";
import TranslatableField from "./TranslatableField";

const EMPTY = { name: { en: "", ar: "" }, parentId: "", isActive: true };

/** Create or edit one product category. `category` null means create. */
export default function ProductCategoryModal({ category, categories, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = Boolean(category);

  useEffect(() => {
    if (!open) return;

    setValues(
      category
        ? {
            name: {
              en: category.translations?.name?.en ?? "",
              ar: category.translations?.name?.ar ?? "",
            },
            parentId: category.parent_id ?? "",
            isActive: Boolean(category.is_active),
          }
        : EMPTY
    );
    setErrors({});
    setBanner("");
  }, [open, category]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, saving]);

  if (!open) return null;

  // A category can be neither its own parent nor its own child's child.
  const parentOptions = categories.filter(
    (option) => option.id !== category?.id && option.parent_id !== category?.id
  );

  const submit = async (e) => {
    e.preventDefault();
    setBanner("");

    const clientErrors = {};
    if (!values.name.en.trim()) clientErrors["name.en"] = [t("products.form.errors.nameEn")];
    if (!values.name.ar.trim()) clientErrors["name.ar"] = [t("products.form.errors.nameAr")];

    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      const saved = editing
        ? await updateProductCategory(category.id, values)
        : await createProductCategory(values);

      onSaved(saved, editing);
      onClose();
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("productCategories.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} noValidate>
          <div className="flex items-center justify-between border-b border-line px-5 py-3">
            <h2 className="text-sm font-semibold">
              {editing ? t("productCategories.editTitle") : t("productCategories.createTitle")}
            </h2>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              aria-label={t("common.close")}
              className="rounded p-1 text-ink-muted hover:bg-raised hover:text-ink-2"
            >
              ✕
            </button>
          </div>

          <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
            {banner && (
              <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            <fieldset disabled={saving} className="space-y-4">
              <TranslatableField
                name="name"
                label={t("productCategories.name")}
                value={values.name}
                onChange={(name) => setValues((v) => ({ ...v, name }))}
                errors={errors}
                required
              />

              <div>
                <label className="field-label" htmlFor="category-parent">
                  {t("productCategories.parent")}
                </label>
                <select
                  id="category-parent"
                  className="field-input"
                  value={values.parentId}
                  onChange={(e) => setValues((v) => ({ ...v, parentId: e.target.value }))}
                >
                  <option value="">{t("productCategories.noParent")}</option>
                  {parentOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.parent ? `${option.parent.name} › ${option.name}` : option.name}
                    </option>
                  ))}
                </select>
                {errors.parent_id?.[0] && <p className="field-error">{errors.parent_id[0]}</p>}
              </div>

              <div className="rounded-lg border border-line p-3">
                <Toggle
                  id="category-active"
                  checked={values.isActive}
                  onChange={(checked) => setValues((v) => ({ ...v, isActive: checked }))}
                  label={t("products.form.active")}
                  error={errors.is_active?.[0]}
                />
              </div>
            </fieldset>
          </div>

          <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
            <button type="button" onClick={onClose} disabled={saving} className="btn-secondary">
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? t("products.form.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
